import React from 'react';
import { Box, SelectChangeEvent } from '@mui/material';
import { appContext } from '../../../AppContext';
import IbssTextField from '../../Inputs/TextField/IbssTextField';
import { IbssComponent } from '../../Core/BaseComponent/IbssComponent';
import IbssInputDropDown, { ISelectOption } from '../../Inputs/SelectList/IbssInputDropDown';
import { Constants } from '../../../Common/Constants';
import { VisitorField, VisitorFieldHelper } from './Visitor';
import { IFilterOptions } from './EditHostDetails';
import IbssFormControl from '../FormControl/IbssFormControl';

export default class EditVisitorDetails extends IbssComponent<IProps, IState>
{
    private get labels() { return appContext().labels; }
    private fieldHelper: VisitorFieldHelper;

    constructor(props: IProps)
    {
        super(props);
        this.fieldHelper = new VisitorFieldHelper(props.fields);
        this.state = {
            hasNameChanged: false,
            hasCompanyChanged: false,
            hasEmailChanged: false,
            hasVisitorTypeChanged: false,
        };
    }

    public resetValidation(): void
    {
        this.setState({
            hasNameChanged: false,
            hasCompanyChanged: false,
            hasEmailChanged: false,
            hasVisitorTypeChanged: false,
        });
    }

    public async componentDidMount(): Promise<void>
    {
        this.props.onValidate(this.isValid());
    }

    public componentDidUpdate(prevProps: IProps): void
    {
        if (this.isValid(prevProps) != this.isValid())
        {
            this.props.onValidate(this.isValid());
        }
    }

    private handleFirstNameChange(event: React.ChangeEvent<HTMLInputElement>)
    {
        this.setState({ hasNameChanged: true });
        this.props.onFirstNameChange(event);
    }

    private handleLastNameChange(event: React.ChangeEvent<HTMLInputElement>)
    {
        this.setState({ hasNameChanged: true });
        this.props.onLastNameChange(event);
    }

    private handleCompanyChange(event: React.ChangeEvent<HTMLInputElement>)
    {
        this.setState({ hasCompanyChanged: true });
        this.props.onCompanyChange(event);
    }

    private handleEmailChange(event: React.ChangeEvent<HTMLInputElement>)
    {
        this.setState({ hasEmailChanged: true });
        this.props.onEmailChange(event);
    }
    private handleVisitorTypeChange(event: SelectChangeEvent)
    {
        this.setState({ hasVisitorTypeChanged: true });
        this.props.onVisitorTypeChange(event);
    }
    private getSelectedVisitorTypeValue(): string 
    {
        const { visitorTypes, visitorTypeId } = this.props;
        return visitorTypes?.some(visitorType => visitorType.value === visitorTypeId) ? visitorTypeId! : "None";
    }

    private isEmailValid(props?: IProps): boolean
    {
        const currentProps = props ?? this.props;
        const email = (currentProps.email ?? '').trim();
        const emailNotRequired = currentProps.emailNotRequired;
        const emailRegexValid = Constants.emailExpression?.test(email);

        if (!this.fieldHelper.has(VisitorField.Email))
        {
            return true;
        }

        if (emailNotRequired)
        {
            return email === "" || !!emailRegexValid;
        }

        return email !== "" && !!emailRegexValid;
    }


    private isValid(props?: IProps) { return this.isNameValid(props) && this.isCompanyValid(props) && this.isEmailValid(props) }
    private isNameValid(props?: IProps) { return (!this.fieldHelper.has(VisitorField.Name) || ((props ?? this.props).firstName ?? '').trim() != "" || ((props ?? this.props).lastName ?? '').trim() != ""); }
    private isCompanyValid(props?: IProps) { return (!this.fieldHelper.has(VisitorField.Company) || ((props ?? this.props).company ?? '').trim() != ""); }

    public render(): JSX.Element
    {
        return (
            <>
                {
                    (this.fieldHelper.has(VisitorField.Name) || this.fieldHelper.has(VisitorField.Company) || this.fieldHelper.has(VisitorField.Email) || this.fieldHelper.has(VisitorField.VisitorType) || this.fieldHelper.has(VisitorField.Pacs)) &&
                    <h3>{this.labels.HubLabelVisitorDetails}</h3>
                }
                {
                    this.fieldHelper.has(VisitorField.Name) &&
                    <Box display={'flex'} sx={{ '& > :not(style)': { my: 1, mr: 1 }, }}>
                        <IbssTextField
                            fullWidth
                            name="visitorFirstName"
                            label={this.labels.HubLabelFirstName}
                            value={this.props.firstName ?? ''}
                            variant="outlined"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleFirstNameChange(event)}
                            disabled={this.props.disableFields}
                            error={this.state.hasNameChanged && !this.isNameValid()}
                            helperText={this.state.hasNameChanged && !this.isNameValid() ? this.labels.funcFirstOrLastNameRequired_L : ""}
                        />
                        <IbssTextField
                            fullWidth
                            name="visitorLastName"
                            label={this.labels.HubLabelLastName}
                            value={this.props.lastName ?? ''}
                            variant="outlined"
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleLastNameChange(event)}
                            disabled={this.props.disableFields}
                            error={this.state.hasNameChanged && !this.isNameValid()}
                        />
                    </Box>
                }
                {
                    this.fieldHelper.has(VisitorField.Company) &&
                    <IbssTextField
                        fullWidth
                        required
                        name="company"
                        className='mt-3'
                        label={this.labels.HubLabelCompany}
                        value={this.props.company ?? ''}
                        variant="outlined"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleCompanyChange(event)}
                        disabled={this.props.disableFields}
                        error={this.state.hasCompanyChanged && !this.isCompanyValid()}
                        helperText={this.state.hasCompanyChanged && !this.isCompanyValid() ? this.labels.HubLabelCompanyNamePlaceholder : ""}
                    />
                }
                {
                    this.fieldHelper.has(VisitorField.Email) &&
                    <IbssTextField
                        fullWidth
                        required={!this.props.emailNotRequired}
                        name="visitorEmail"
                        label={this.labels.funcDelegateEmail_S}
                        value={this.props.email ?? ''}
                        className='mt-3'
                        variant="outlined"
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.handleEmailChange(event)}
                        disabled={this.props.disableFields}
                        error={this.state.hasEmailChanged && !this.isEmailValid()}
                        helperText={this.state.hasEmailChanged && !this.isEmailValid() ? this.labels.HubLabelInvalidEmailError : ""}
                    />
                }
                {
                    this.fieldHelper.has(VisitorField.Pacs) &&
                    <IbssTextField
                        fullWidth
                        name="pacsId"
                        label={this.labels.HubLabelPacs}
                        value={this.props.pacs ?? ''}
                        variant="outlined"
                        className='mt-3'
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => this.props.onPacsChanged(event)}
                        disabled={this.props.disableFields}
                    />
                }
                {
                    this.props.isFlex && this.props.buildingOptions &&
                    <IbssFormControl fullWidth sx={{mt: 2}}>
                        <IbssInputDropDown
                            inputLabel={this.labels.HubLabelBuilding}
                            options={this.props.buildingOptions}
                            value={this.props.buildingId}
                            id={"buildingSelection"}
                            onChange={(e: SelectChangeEvent<string>) => this.props.buildingChanged?.(e.target.value)}
                            disabled={this.props.disableFields}
                            fullWidth
                            />
                    </IbssFormControl>
                }
                {
                    this.fieldHelper.has(VisitorField.ArrivalLocation) &&
                    <Box my={2}>
                        <IbssInputDropDown
                            onChange={e => this.props.onArrivalLocationChange(e)}
                            value={this.props.arrivalLocationId ?? ''}
                            inputLabel={this.labels.HubLabelArrivalLocationId}
                            id="Arrival LocationId"
                            options={this.props.arrivalLocationOptions ?? []}
                            disabled={this.props.disableFields}
                        />
                    </Box>
                }
                {
                    this.fieldHelper.has(VisitorField.VisitorType) &&
                    <Box mt={2}>
                        <IbssInputDropDown
                            id="Visitor Type"
                            inputLabel={this.labels.funcTypeOfVisitor_S}
                            value={this.getSelectedVisitorTypeValue()}
                            options={this.props.visitorTypes ?? []}
                            onChange={e => this.handleVisitorTypeChange(e)}
                            disabled={this.props.disableFields}
                        />
                    </Box>
                }
            </>
        )
    }
}

interface IProps
{
    fields?: VisitorField;
    firstName?: string;
    lastName?: string;
    company?: string;
    email?: string;
    pacs?: string;
    arrivalLocationId?: string;
    visitorTypeId?: string;
    visitorTypes?: ISelectOption<string, string>[];
    arrivalLocationOptions?: IFilterOptions[];
    isFlex?: boolean;
    buildingId: number;
    buildingOptions?: ISelectOption<string, number>[];
    onValidate: (isValid: boolean) => void;
    onFirstNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onLastNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onCompanyChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onEmailChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onPacsChanged: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onArrivalLocationChange: (event: SelectChangeEvent) => void;
    onVisitorTypeChange: (event: SelectChangeEvent) => void;
    buildingChanged?: (buildingId: string) => void;
    disableFields?: boolean;
    emailNotRequired?: boolean;
}

interface IState
{
    hasNameChanged: boolean;
    hasCompanyChanged: boolean;
    hasEmailChanged: boolean;
    hasVisitorTypeChanged: boolean;
}
